import React from 'react';

import { Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';

import {
  TypeOfUser,
  logos,
  PharmacistLegendVariant,
  PharmacyLegendVariant,
  RecurrenceActions,
  getTypeOfUser,
  userPreferredTimeFormat,
  BookingState,
} from '@pharmaplan/common';
import { ReactComponent as HourRate } from '@pharmaplan/common/assets/icons/hourlyRateRow.svg';
import { ReactComponent as Location } from '@pharmaplan/common/assets/icons/location.svg';
import { ReactComponent as Software } from '@pharmaplan/common/assets/icons/softwareBasic.svg';
import { ReactComponent as Time } from '@pharmaplan/common/assets/icons/timeRow.svg';
import { ReactComponent as FavouriteNotActive } from '@pharmaplan/common/assets/icons/favouriteNotActive.svg';
import { ReactComponent as FavouriteActive } from '@pharmaplan/common/assets/icons/favourtieActive.svg';

import useSelfService from '../../../hooks/useSelfService';
import useDrawerNavigation from '../../../hooks/useDrawerNavigation';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import useDetailsNavigation from '../../../hooks/Admin/useDetailsNavigation';
import useAdmin from '../../../hooks/useAdmin';
import { getHourMinute } from '../../Reports/helpers';
import ICalendar from '../../../types/ICalendar';
import { setDialog } from '../../../reducers/dialogReducer';
import UpdateModal from '../../Modals/UpdateModal';
import { toUTC } from '../MainCalendar/helpers';
import { isPast } from '../../../helpers/Functions';
import { ReplacementTypes } from '../../../helpers/Constants';
import strings from '../../../localization';
import { updateTitle } from '../MainCalendar/CustomMonthEvent/helper';

import classes from './style';
import Watchlisted from '../../common/Watchlist/Watchlisted';

interface IEventChip {
  event: ICalendar;
  typeOfUser: TypeOfUser;
}

const { pharmacist, pharmacy } = TypeOfUser;
const { availability, vacation } = PharmacistLegendVariant;
const { requested, booked } = PharmacyLegendVariant;
const { PendingWithPharmacist } = BookingState;

const EventChip = ({ event, typeOfUser }: IEventChip) => {
  const dispatch = useAppDispatch();

  const allLogos = useAppSelector(logos);
  const userType = useAppSelector(getTypeOfUser);

  const {
    startDate,
    type,
    bookingId,
    bookingState,
    id,
    recurrenceId,
    logoId,
    endDate,
    hourlyRate,
    pharmacyName,
    replacementType,
    softwareName,
    pharmacistName,
    favourite,
    workShiftId,
    professionalName,
    pharmacyId,
    city,
    isInWatchlist,
    isAutoSyncRecord,
  } = event ?? {};

  const {
    itemContainer,
    itemNameContainer,
    hideBorder,
    icon,
    textOverflowFixes,
    flex,
    mt,
    eventContainer,
    eventTitle,
    logoWidth,
  } = classes ?? {};

  const { isSelfService } = useSelfService();

  const preventClick = isPast(toUTC(startDate));

  const timeFormat = useAppSelector(userPreferredTimeFormat);

  // ONLY SHOW WHEN EVENT IS NOT IN PAST AND CREATE EVENTS IS DISABLED
  const showEvent = !preventClick;

  const logo = allLogos?.find(
    (item: { logoId: string }) =>
      item.logoId === logoId,
  )?.image;

  const { openDrawer } = useDrawerNavigation();
  const { handleNavigation } = useDetailsNavigation();
  const { isAdmin } = useAdmin();

  const isPendingWithPharmacist = type === requested && bookingState === PendingWithPharmacist;
  const isAvailability = type === availability;
  const isVacation = type === vacation;
  const isBooked = type === booked;

  const isPharmacy = typeOfUser === pharmacy;
  const isPharmacist = typeOfUser === pharmacist;

  const isPharmacyOrAvailabilityOrVacation = isAvailability || isVacation || isPharmacy;
  const isPharmacyAndBooked = isBooked && isPharmacy;
  const isReplacementTypeValid = replacementType !== null && replacementType !== undefined;
  const isHourlyRateValid = hourlyRate !== null && hourlyRate !== undefined;

  const showFavoriteWhen = isPharmacist && !isAvailability && !isVacation;
  const showFavorite = showFavoriteWhen && favourite;

  const time = ` ${getHourMinute(startDate, timeFormat)} - ${getHourMinute(
    endDate,
    timeFormat,
  )}`;

  const getEventId = () => {
    if (isPendingWithPharmacist) {
      return bookingId;
    }

    return id;
  };

  const eventId = getEventId();

  const eventSelect = () => {
    isAdmin
      ? handleNavigation({
        id,
        pharmacyId,
        eventType: type,
        date: startDate,
        workshiftId: workShiftId,
      })()
      : openDrawer({
        eventIds: [eventId ?? ''],
        type,
        bookingState,
        openDetails: isPharmacy,
        isAutoSyncIDMap: {
          [eventId ?? '']: !!isAutoSyncRecord,
        },
      });
  };

  const handlePress = () => {
    if (!showEvent && !isAdmin) {
      return;
    }

    Object.hasOwn(updateTitle(isSelfService), type) && recurrenceId
      ? dispatch(
        setDialog({
          showCloseButton: false,
          Component: (
            <UpdateModal
              type={userType}
              handleSelect={(selected: RecurrenceActions) =>
                openDrawer({
                  eventIds: [id],
                  recurrenceType: selected,
                  recurrenceId,
                  type,
                })}
            />
          ),
          heading: {
            title: '',
          },
        }),
      )
      : eventSelect();
  };

  const renderEndItem = () => {
    if (isAdmin) {
      return (
        <Box sx={[flex]}>
          <Typography component="span" sx={textOverflowFixes}>
            {professionalName ?? pharmacistName}
          </Typography>
        </Box>
      );
    }

    return (
      showFavoriteWhen && (
        <Box sx={[flex, mt]}>
          {isInWatchlist && <Watchlisted />}
          {showFavorite ? <FavouriteActive /> : <FavouriteNotActive />}
        </Box>
      )
    );
  };

  return (
    <Box sx={eventContainer(type, typeOfUser.toLowerCase() as TypeOfUser)}>
      <Grid sx={flex}>
        {isPharmacyOrAvailabilityOrVacation && (
          <Typography sx={eventTitle(type, typeOfUser)}>
            {strings[type]}
          </Typography>
        )}

        {logo && (
          <Box
            sx={[itemContainer, itemNameContainer, hideBorder]}
            component="span"
          >
            <Box component="img" style={logoWidth} src={logo} alt="logo" />
          </Box>
        )}
      </Grid>

      <Grid container flexDirection="row" onClick={handlePress}>
        <Grid item sx={flex} component="span">
          {!!pharmacyName && (
            <Box sx={itemContainer}>
              <Typography sx={[textOverflowFixes]}>{pharmacyName}</Typography>
            </Box>
          )}

          {isReplacementTypeValid
            && (isPharmacyAndBooked ? (
              <Box sx={itemContainer}>{professionalName ?? pharmacistName}</Box>
            ) : (
              <Box sx={itemContainer} component="span">
                {ReplacementTypes()[replacementType]}
              </Box>
            ))}

          {!!city && (
            <Box sx={itemContainer} component="span">
              <Location style={icon} />
              <Box sx={textOverflowFixes} component="span">
                {city}
              </Box>
            </Box>
          )}

          {!isVacation && (
            <Box sx={itemContainer} component="span">
              <Time style={icon} />
              <Box sx={textOverflowFixes} component="span">
                {time}
              </Box>
            </Box>
          )}
        </Grid>

        <Grid item sx={flex}>
          {isHourlyRateValid && (
            <Box sx={itemContainer} component="span">
              <HourRate style={icon} />

              <Box sx={textOverflowFixes} component="span">
                {`$${hourlyRate}`}
              </Box>
            </Box>
          )}

          {softwareName && (
            <Box sx={itemContainer} component="span">
              <Software style={icon} />
              {softwareName}
            </Box>
          )}
        </Grid>
      </Grid>
      {renderEndItem()}
    </Box>
  );
};

export default EventChip;
